<script setup lang="ts">
  import { computed } from 'vue';

  type IconRadioVariant = 'big' | 'medium' | 'small';

  const VARIANT_CLASSES: Record<
    IconRadioVariant,
    { wrapper: string; dot: string; innerDot: string; label: string }
  > = {
    big: {
      wrapper: 'font-semibold',
      dot: 'w-5 h-5 peer-checked:border-6',
      innerDot: 'w-2 h-2',
      label: 'ml-2',
    },
    medium: {
      wrapper: 'font-medium',
      dot: 'w-4 h-4 peer-checked:border-[5px]',
      innerDot: 'w-1.5 h-1.5',
      label: 'ml-2',
    },
    small: {
      wrapper: 'font-medium text-xs/5',
      dot: 'w-3.5 h-3.5 peer-checked:border-[5px]',
      innerDot: 'w-1.5 h-1.5',
      label: 'ml-1.5',
    },
  };

  const props = defineProps({
    modelValue: String,
    value: String,
    label: String,
    name: {
      type: String,
      default: 'radioGroup',
    },
    variant: {
      type: String,
      default: 'big',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(['update:modelValue']);

  const resolveVariant = (variant: string): IconRadioVariant =>
    Object.hasOwn(VARIANT_CLASSES, variant) ? (variant as IconRadioVariant) : 'small';

  const variantClasses = computed(() => VARIANT_CLASSES[resolveVariant(props.variant)]);
</script>

<template>
  <div
    class="text-gray-07 text-sm/5 text-left flex items-center"
    :class="[variantClasses.wrapper, { 'opacity-50': disabled }]"
  >
    <label
      class="inline-flex items-center"
      :class="disabled ? 'cursor-not-allowed' : 'cursor-pointer'"
    >
      <input
        :id="value"
        type="radio"
        :name="name"
        :value="value"
        :checked="modelValue === value"
        :disabled="disabled"
        class="hidden peer"
        @change="$emit('update:modelValue', value)"
      >
      <span
        class="inline-block rounded-full border border-light-gray-xx peer-checked:border-[color-mix(in_srgb,var(--color-white-01),var(--theme-secondary))] relative peer-checked:bg-white-01"
        :class="variantClasses.dot"
      >
        <span class="absolute inset-0 flex items-center justify-center">
          <span
            :class="variantClasses.innerDot"
            class="rounded-full bg-white-01 opacity-0 peer-checked:opacity-100"
          />
        </span>
      </span>
      <span
        v-if="label"
        :class="variantClasses.label"
      >{{ label }}</span>
    </label>
  </div>
</template>
